import type { Platform } from '@/shared/hooks/use-platform'

export type Shortcut = readonly string[]

const MODIFIER_KEYS = new Set([
  'mod',
  'meta',
  'cmd',
  'command',
  'control',
  'ctrl',
  'shift',
  'alt',
  'option',
  'opt',
])

const MAC_SYMBOL_MAP: Record<string, string> = {
  mod: '⌘',
  meta: '⌘',
  cmd: '⌘',
  command: '⌘',
  control: '⌃',
  ctrl: '⌃',
  shift: '⇧',
  alt: '⌥',
  option: '⌥',
  opt: '⌥',
}

const DEFAULT_SYMBOL_MAP: Record<string, string> = {
  mod: 'Ctrl',
  meta: 'Meta',
  cmd: 'Meta',
  command: 'Meta',
  control: 'Ctrl',
  ctrl: 'Ctrl',
  shift: 'Shift',
  alt: 'Alt',
  option: 'Alt',
  opt: 'Alt',
}

const SPECIAL_KEY_DISPLAY: Record<string, string> = {
  escape: 'Esc',
  esc: 'Esc',
  enter: 'Enter',
  return: 'Enter',
  tab: 'Tab',
  space: 'Space',
  spacebar: 'Space',
  backspace: 'Backspace',
  delete: 'Delete',
  arrowup: '↑',
  arrowdown: '↓',
  arrowleft: '←',
  arrowright: '→',
  pageup: 'Page Up',
  pagedown: 'Page Down',
  home: 'Home',
  end: 'End',
}

function normalizeKey(key: string) {
  return key.trim().toLowerCase()
}

function getModifierSymbol(key: string, platform: Platform): string | undefined {
  const normalized = normalizeKey(key)
  const map = platform === 'mac' ? MAC_SYMBOL_MAP : DEFAULT_SYMBOL_MAP
  return map[normalized]
}

function getMainKeyDisplay(key: string) {
  const normalized = normalizeKey(key)
  if (SPECIAL_KEY_DISPLAY[normalized]) {
    return SPECIAL_KEY_DISPLAY[normalized]
  }

  if (normalized.length === 1) {
    return normalized.toUpperCase()
  }

  if (normalized.startsWith('f') && !Number.isNaN(Number(normalized.substring(1)))) {
    return normalized.toUpperCase()
  }

  return normalized.toUpperCase()
}

export function getShortcutDisplayParts(shortcut: Shortcut, platform: Platform): string[] {
  const parts: string[] = []
  shortcut.forEach((key) => {
    const modifier = getModifierSymbol(key, platform)
    if (modifier) {
      parts.push(modifier)
      return
    }

    parts.push(getMainKeyDisplay(key))
  })

  return parts
}

function normalizeEventKey(event: KeyboardEvent) {
  const key = event.key.toLowerCase()
  if (key === ' ') {
    return 'space'
  }

  return key
}

type ExpectedState = {
  meta: boolean
  ctrl: boolean
  shift: boolean
  alt: boolean
}

function buildExpectedState(shortcut: Shortcut, platform: Platform): {
  state: ExpectedState
  mainKeys: string[]
} {
  const state: ExpectedState = {
    meta: false,
    ctrl: false,
    shift: false,
    alt: false,
  }

  const mainKeys: string[] = []

  shortcut.forEach((key) => {
    const normalized = normalizeKey(key)
    switch (normalized) {
      case 'mod':
        if (platform === 'mac') {
          state.meta = true
        } else {
          state.ctrl = true
        }
        break
      case 'meta':
      case 'cmd':
      case 'command':
        state.meta = true
        break
      case 'control':
      case 'ctrl':
        state.ctrl = true
        break
      case 'shift':
        state.shift = true
        break
      case 'alt':
      case 'option':
      case 'opt':
        state.alt = true
        break
      default: {
        switch (normalized) {
          case 'spacebar':
            mainKeys.push('space')
            break
          case 'esc':
            mainKeys.push('escape')
            break
          case 'return':
            mainKeys.push('enter')
            break
          default:
            mainKeys.push(normalized)
            break
        }
        break
      }
    }
  })

  return { state, mainKeys }
}

function modifierMismatch(event: KeyboardEvent, expected: ExpectedState) {
  if (event.metaKey !== expected.meta) {
    return true
  }

  if (event.ctrlKey !== expected.ctrl) {
    return true
  }

  if (event.shiftKey !== expected.shift) {
    return true
  }

  if (event.altKey !== expected.alt) {
    return true
  }

  return false
}

export function isKeyboardEventMatching(
  event: KeyboardEvent,
  shortcut: Shortcut,
  platform: Platform
): boolean {
  if (!shortcut.length) {
    return false
  }

  const { state, mainKeys } = buildExpectedState(shortcut, platform)

  if (!mainKeys.length) {
    return false
  }

  if (modifierMismatch(event, state)) {
    return false
  }

  const eventKey = normalizeEventKey(event)
  return mainKeys.includes(eventKey)
}

export function isModifierKey(key: string) {
  return MODIFIER_KEYS.has(normalizeKey(key))
}
